import React, { useCallback, useMemo, useReducer } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Form } from '@epoch-ui/components/Form';
import { Modal, ModalContent, ModalContentProps } from '@epoch-ui/components/Modal/modal';
import {
  PlusOneFormValue,
  usePlusOneSchema
} from 'components/discover/event/EventView/PlusOneForm/usePlusOneSchema';
import {
  useConfirmUnsavedChanges,
  UnsavedChangesConfirmationModalProvider
} from 'epoch-components/UnsavedChangesConfirmationModal';
import { useToaster } from 'hooks/useToaster';
import { EventType } from 'types/event.types';

import { useGuestSideSheetContext } from '../../guestSideSheetContext';

import { SelectGuestContent, SelectGuestFormValues } from './SelectGuestContent';
import { PlusOneGuestFormContent } from './PlusOneGuestFormContent';
import { SetNotificationsContent } from './SetNotificationsContent';
import { usePlusOneGuestMutations } from './usePlusOneGuestMutations';
import { plusOneModalReducer, ModalStep, PlusOneModalState } from './plusOneModalReducer';
import { PlusOneGuestModalProvider } from './plusOneGuestModalContext';

const modalContentPropsForStep = (step: ModalStep): ModalContentProps => {
  switch (step) {
    case 'notifications':
      return { size: 'md', className: 'tw-flex tw-flex-col' };
    case 'form':
    case 'selectGuest':
    default:
      return { size: 'lg', className: 'tw-flex tw-max-h-[80vh] tw-flex-col' };
  }
};

export interface PlusOneGuestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGuestAdded?: (plusOneId: number) => void;
}

const PlusOneGuestModalContent: React.FC<PlusOneGuestModalProps> = ({
  open,
  onOpenChange,
  onGuestAdded
}) => {
  const { confirmWhen } = useConfirmUnsavedChanges();
  const {
    selectedPlusOneGuest: plusOneGuest,
    eventOrSessionId: eventId,
    guestData: { guest, event },
    selectedSession,
    handleUpdateRefetch
  } = useGuestSideSheetContext();

  const isMultiSessionEvent = event.eventType === EventType.MULTI_SESSIONS;
  const plusOneConfig = (
    isMultiSessionEvent ? selectedSession?.plusOneConfig : event.plusOneConfig
  )!;
  const preEventQuestions = event.preEventQuestions || [];

  const isEditMode = !!plusOneGuest?.plusOneId;
  const initialStep: ModalStep =
    isMultiSessionEvent && !isEditMode && !event.limitRegistration ? 'selectGuest' : 'form';

  // --- Select guest form (MSE only) ---

  const selectGuestForm = useForm<SelectGuestFormValues>({
    values: {
      selectedPlusOneIds: (guest.plusOnes || []).map((plusOne) => plusOne.plusOneId)
    }
  });

  // --- Reducer ---

  const initialState: PlusOneModalState = {
    currentStep: initialStep,
    previousStep: null,
    pendingFormValues: null,
    pendingSelectedPlusOneIds: null
  };
  const [state, dispatch] = useReducer(plusOneModalReducer, initialState);

  // --- Plus-one form ---

  const { collectQuestionnaireResponses } = plusOneConfig;
  const questionsToShow = collectQuestionnaireResponses ? preEventQuestions : [];

  const { plusOneSchema, formatPlusOne, newPlusOne, parsePlusOneFormValue } = usePlusOneSchema({
    collectQuestionnaireResponses,
    questions: questionsToShow
  });

  // formatPlusOne and newPlusOne generate new uuids each call, so we must memoize the result
  const formValues = useMemo<PlusOneFormValue>(
    () => (plusOneGuest ? formatPlusOne(plusOneGuest) : newPlusOne()),
    [plusOneGuest]
  );

  const plusOneForm = useForm<PlusOneFormValue>({
    resolver: zodResolver(plusOneSchema),
    values: formValues,
    mode: 'onSubmit'
  });

  // --- Reset helpers ---

  const resetFormAndCloseModal = (): void => {
    plusOneForm.reset();
    selectGuestForm.reset();
    dispatch({ type: 'RESET', initialStep });
    onOpenChange(false);
  };

  // --- Mutations ---

  const { showToast } = useToaster();

  const {
    savePlusOneGuest,
    setSessionPlusOneGuests,
    removePlusOneGuest,
    savingPlusOneGuest,
    removingPlusOneGuest,
    settingSessionPlusOneGuests
  } = usePlusOneGuestMutations();

  const { gid } = guest;

  const handleFinalSubmit = useCallback(
    (sendNotifications: boolean) => {
      if (state.previousStep === 'form') {
        if (!state.pendingFormValues) return;
        const updatedPlusOneGuest = parsePlusOneFormValue(state.pendingFormValues);
        savePlusOneGuest({
          variables: {
            input: {
              eventId,
              gid,
              plusOneGuest: updatedPlusOneGuest,
              sendNotifications
            }
          },
          onCompleted: (data) => {
            if (!data.adminUpdatePlusOneGuest.ok) {
              showToast(isEditMode ? 'Failed to update guest' : 'Failed to add guest');
              return;
            }

            showToast(isEditMode ? 'Guest updated successfully' : 'Guest added successfully');
            handleUpdateRefetch();

            if (isMultiSessionEvent && !event.limitRegistration) {
              plusOneForm.reset();
              dispatch({ type: 'RETURN_TO_SELECT_GUEST' });
            } else {
              resetFormAndCloseModal();
              if (!isEditMode) {
                onGuestAdded?.(data.adminUpdatePlusOneGuest.plusOneGuest.plusOneId);
              }
            }
          },
          onError: () => {
            showToast(isEditMode ? 'Failed to update guest' : 'Failed to add guest');
          }
        });
      } else if (state.previousStep === 'selectGuest') {
        if (!state.pendingSelectedPlusOneIds) return;

        setSessionPlusOneGuests({
          variables: {
            input: {
              gid,
              sessionId: eventId,
              plusOneIds: state.pendingSelectedPlusOneIds,
              sendNotifications
            }
          },
          onCompleted: (data) => {
            if (!data.adminSetSessionPlusOneGuests.ok) {
              showToast('Failed to update session guests');
              return;
            }

            showToast('Session guests updated successfully');
            handleUpdateRefetch();
            resetFormAndCloseModal();
          },
          onError: () => {
            showToast('Failed to update session guests');
          }
        });
      }
    },
    [
      savePlusOneGuest,
      setSessionPlusOneGuests,
      showToast,
      state.previousStep,
      state.pendingFormValues,
      state.pendingSelectedPlusOneIds,
      isMultiSessionEvent,
      isEditMode,
      event.limitRegistration,
      eventId,
      gid,
      plusOneForm,
      dispatch,
      resetFormAndCloseModal,
      onGuestAdded,
      handleUpdateRefetch
    ]
  );

  const handleRemoveGuest = useCallback(() => {
    if (!plusOneGuest?.plusOneId) {
      resetFormAndCloseModal();
      return;
    }

    removePlusOneGuest({
      variables: {
        input: {
          eventId,
          gid,
          plusOneId: plusOneGuest.plusOneId
        }
      },
      onCompleted: (data) => {
        if (!data.adminRemovePlusOneGuest.ok) {
          showToast('Failed to remove guest');
          return;
        }

        showToast('Guest removed successfully');
        handleUpdateRefetch();
        resetFormAndCloseModal();
      },
      onError: () => {
        showToast('Failed to remove guest');
      }
    });
  }, [
    removePlusOneGuest,
    showToast,
    resetFormAndCloseModal,
    plusOneGuest?.plusOneId,
    eventId,
    gid,
    handleUpdateRefetch
  ]);

  // --- Modal open/close ---

  const handleModalOpenChange = (isOpen: boolean): void => {
    if (!isOpen) {
      const hasUnsavedChanges = plusOneForm.formState.isDirty || selectGuestForm.formState.isDirty;

      confirmWhen(hasUnsavedChanges, {
        onConfirm: resetFormAndCloseModal
      });
    } else {
      onOpenChange(isOpen);
    }
  };

  // --- Context value ---

  const contextValue = {
    state,
    dispatch,
    handleFinalSubmit,
    handleRemoveGuest,
    savingPlusOneGuest,
    removingPlusOneGuest,
    settingSessionPlusOneGuests
  };

  // --- Render ---

  return (
    <Modal open={open} onOpenChange={handleModalOpenChange}>
      <ModalContent {...modalContentPropsForStep(state.currentStep)}>
        <PlusOneGuestModalProvider value={contextValue}>
          {state.currentStep === 'selectGuest' && (
            <Form {...selectGuestForm}>
              <SelectGuestContent />
            </Form>
          )}

          {state.currentStep === 'form' && (
            <Form {...plusOneForm}>
              <PlusOneGuestFormContent />
            </Form>
          )}

          {state.currentStep === 'notifications' && <SetNotificationsContent />}
        </PlusOneGuestModalProvider>
      </ModalContent>
    </Modal>
  );
};

export const PlusOneGuestModal: React.FC<PlusOneGuestModalProps> = (props) => {
  return (
    <UnsavedChangesConfirmationModalProvider>
      <PlusOneGuestModalContent {...props} />
    </UnsavedChangesConfirmationModalProvider>
  );
};
